import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { IDataRequest, IInProcessDataRequest, IRequestedStudy } from '@vivli/features/data-requests/infrastructure/interface';
import {
    AddRequestedStudyDuringDraftModalComponent,
    AddStudyInProcessComponent,
    AddUnlistedStudyModalComponent,
    RequestedStudiesComponent
} from '@vivli/features/studies/components';
import { FieldHeaderComponent, QuestionMarkComponent } from '@vivli/shared/components';
import {
    useActiveUserContext,
    useAssignedAppTypeContext,
    useDataPackageApiContext,
    useDataPackageFileApiContext,
    useDataRequestPermissionsContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useToastApiContext
} from '@vivli/core/infrastructure/context';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { useCsvExportHook } from '@vivli/shared/infrastructure/hook';
import { DataRequestStatusEnum, RequestedStudyTypeEnum, StudyRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { forkJoin, of } from 'rxjs';
import { first } from 'rxjs/operators';
import { Color, Size } from '@vivli/shared/theme';
import { useRequestedStudyPermissions } from '@vivli/features/studies/infrastructure/hook';
import { PermissionsEnum, TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { useTranslation } from 'react-i18next';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import { IModalConfirmOptions } from '@vivli/shared/infrastructure/interface';
import { useNavigate } from 'react-router';
import { DTIRequestedStudiesFeature } from '../../../../../../apps/vivli-e2e/src/fixtures/data-test-id.constants';
import { DTICommonConst } from '@vivli/shared/infrastructure/constants';
import { ButtonView, ExportCsvButtonView, LoadIndicatorCenteredView } from '@vivli/shared/views';
import { useWatch } from 'react-hook-form';
import { useDataRequestFormUpdate } from '@vivli/features/data-requests/infrastructure/hook';

const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: Size.INSET_SIZE
};

const exportButtonStyle: CSSProperties = {
    position: 'relative',
    height: 'auto',
    right: 0
};

const genericStyle: CSSProperties = {
    marginTop: Size.FIELD_SEPARATION,
    marginBottom: Size.FIELD_SEPARATION
};

const oneAcrossStyleSmallNoBox: CSSProperties = {
    ...genericStyle,
    width: '200',
    minWidth: '200',
    marginLeft: 'auto',
    marginRight: 'auto'
};

const addStudyContainerStyle: CSSProperties = {
    marginLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '-10px'
};

const gridContainerStyle: CSSProperties = { marginLeft: 50 };

const sectionStyle: CSSProperties = { marginBottom: 20 };

const sectionStyleWithTop: CSSProperties = { marginBottom: 20, marginTop: 40 };

const headerContainerStyle: CSSProperties = { display: 'flex', flexDirection: 'row' };

const flexContainerStyle: CSSProperties = { display: 'flex', paddingTop: '10px' };

const addStudyButtonStyle: CSSProperties = {
    height: '21px',
    padding: '0',
    paddingRight: '0',
    minWidth: '70px',
    backgroundColor: Color.WHITE,
    borderColor: '#A0A0A0',
    borderWidth: 1,
    borderStyle: 'solid',
    color: '#A0A0A0'
};

const buttonContainerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    height: '50px',
    marginLeft: '20px'
};

export const RequestedStudiesFeature = ({ editable }: { editable: boolean }) => {
    const isPreppingZipRef = useRef(false);
    const [zipFileUri, setZipFileUri] = useState<string>(null);
    const { updateDataRequest } = useDataRequestContext();
    const dataPackageFileService = useDataPackageFileApiContext();
    const dataPackageService = useDataPackageApiContext();
    const { requestedStudyIpdIsDownloadable, userIsDuaValidated } = useRequestedStudyPermissions();
    const { userIsOwner, isDuaValidatedRequest, userIsDataContributor } = useDataRequestPermissionsContext();
    const toastService = useToastApiContext();
    const dataRequestsService = useDataRequestsApiContext();
    const { hasPermission } = useActiveUserContext();
    const { exportRows } = useCsvExportHook();
    const modalService = useModalApiContext();
    const { t } = useTranslation();
    const { isAmr } = useAssignedAppTypeContext();
    const { organizations } = useOrganizations();
    const [inProcessDataRequest, setInProcessDataRequest] = useState<IInProcessDataRequest>(null);
    const navigate = useNavigate();
    const handleDataRequestFormUpdate = useDataRequestFormUpdate();

    const dataRequest = useWatch<IDataRequest>() as IDataRequest;

    const uppercaseStudiesString = t(TranslationKeyEnum.studies).toUpperCase();
    const noStudiesFound = <div>No {t(TranslationKeyEnum.studies)} Found</div>;
    const addLabel = 'Add Vivli Study';
    const showRequesterAddButton = userIsOwner(dataRequest) && editable && !isAmr;

    const requestedStudiesHaveIpdAvailable = useMemo(
        () => dataRequest.requestedStudies.some((r) => requestedStudyIpdIsDownloadable(r)),
        [dataRequest.requestedStudies, requestedStudyIpdIsDownloadable]
    );
    const anyDownloadableRequestedStudies = useMemo(
        () => dataRequest.requestedStudies.some((r) => r.downloadableRequestedStudyIPDDataPackage),
        [dataRequest.requestedStudies]
    );
    const studyName = t(TranslationKeyEnum.study);
    const successMessage = `${studyName} added to In-Process Data Request`;
    const userHasDuaValidation = useMemo(() => userIsDuaValidated(dataRequest), [dataRequest, userIsDuaValidated]);
    const ipdDownloadAllowed = useMemo(
        () =>
            isDuaValidatedRequest(dataRequest) &&
            !userIsDataContributor(dataRequest, organizations) && // data contributors are ALLOWED to download IPD, but are interested in the list, not the IPD
            requestedStudiesHaveIpdAvailable && // at least one study has IPD available
            anyDownloadableRequestedStudies && // at least one study has IPD that is downloadable
            userHasDuaValidation,
        [
            isDuaValidatedRequest,
            dataRequest,
            userIsDataContributor,
            organizations,
            requestedStudiesHaveIpdAvailable,
            anyDownloadableRequestedStudies,
            userHasDuaValidation
        ]
    ); // user has DUA validation (no downloads without it!)
    //TODO PERMFE - redo with permissions+
    const isVivliAdmin = hasPermission(PermissionsEnum.DrActAdv);
    const canAddInProcess = useMemo(
        () => isVivliAdmin && dataRequest.status !== 'Cancelled' && dataRequest.status !== 'Archived',
        [isVivliAdmin, dataRequest.status]
    );

    const downloadStudyFiles = () => {
        if (!ipdDownloadAllowed) {
            toastService.info('Data not yet available.');
        }

        if (zipFileUri) {
            window.open(zipFileUri);
        } else {
            isPreppingZipRef.current = true;
            toastService.info('Preparing zip file for download...please click Download again if required', { autoClose: false });
        }

        const sub = dataPackageFileService
            .downloadDataRequestStudyFiles(dataRequest.id)
            .pipe(first((fileUri) => fileUri?.length > 0))
            .subscribe((fileUri) => {
                if (fileUri?.length <= 0) {
                    toastService.dismiss();
                    toastService.info('No zip file available for download.');
                    return;
                }
                setZipFileUri(fileUri);
            });
    };

    const downloadCsv = () => {
        const studies = dataRequest.requestedStudies;

        const statusChecks = studies.map((study: IRequestedStudy) => {
            const isUnlistedOrTro = study.requestedStudyType === RequestedStudyTypeEnum.Unlisted || study.thisRequestOnlyIpd;
            if (isUnlistedOrTro || !study.studyIPDDataPackageId) {
                return of(false);
            }
            return dataPackageService.getDataPackageSubmitStatus(study.studyIPDDataPackageId).pipe(first());
        });

        forkJoin(statusChecks).subscribe((statuses) => {
            const exportData = studies.map((study: IRequestedStudy, i: number) => ({
                sponsorId: study.sponsorProtocolId,
                studyId: study.nctId,
                hasUploadedIpd: statuses[i], //upload status of TEMPLATE study IPD, if it exists
                title: study.studyTitle,
                pi: study.principalInvestigatorName,
                sponsorName: study.sponsorName,
                contributorName: study.organizationName,
                irpName: study.approvalBodyOrgName,
                requestId: dataRequest.doiStem
            }));

            const filename = 'RequestedStudies.csv';
            const headers = [
                'Sponsor ID',
                'Study Id',
                'Ipd Uploaded',
                'Study Title',
                'Principal Investigator',
                'Sponsor Name',
                'Data Contributor Name',
                'IRP/Approver Name',
                'Data Request Id'
            ];

            exportRows(filename, exportData, headers);
        });
    };

    const addUnlistedStudy = () => {
        const modalId = modalService.custom(
            <AddUnlistedStudyModalComponent
                dataRequestId={dataRequest.id}
                onClose={() => modalService.dismiss(modalId)}
                onStudyAdded={handleDataRequestFormUpdate}
                isInProcess={canAddInProcess}
            />,
            { style: { minWidth: '910px' } }
        );
    };

    const addRequestedStudyDuringDraft = () => {
        const modalId = modalService.custom(
            <AddRequestedStudyDuringDraftModalComponent
                dataRequestId={dataRequest.id}
                onClose={() => modalService.dismiss(modalId)}
                onStudyAdded={handleDataRequestFormUpdate}
            />,
            { style: { minWidth: '910px' } }
        );
    };

    const addInProcessStudy = (id: string) => {
        const inProcess = dataRequestsService
            .addStudyToDataRequestInProcess({ studySponsorIdOrNctId: id, dataRequestId: dataRequest.id })
            .pipe(first())
            .subscribe((inProcRequest) => {
                setInProcessDataRequest(inProcRequest);
                handleDataRequestFormUpdate(dataRequest);
            });
    };

    const externalStudies = useMemo(
        () => dataRequest.requestedStudies?.filter((s) => s.requestedStudyType === RequestedStudyTypeEnum.External),
        [dataRequest.requestedStudies]
    );
    const unlistedStudies = useMemo(
        () => dataRequest.requestedStudies?.filter((s) => s.requestedStudyType === RequestedStudyTypeEnum.Unlisted),
        [dataRequest.requestedStudies]
    );
    const listedStudies = useMemo(
        () => dataRequest.requestedStudies?.filter((s) => s.requestedStudyType === RequestedStudyTypeEnum.Listed),
        [dataRequest.requestedStudies]
    );

    const showQuestionMarkIcon = () => {
        if (!isAmr) {
            return <QuestionMarkComponent size={25} onClick={() => window.open('https://vivli.org/requested-study-types/', '_blank')} />;
        }
    };

    useEffect(() => {
        if (!inProcessDataRequest) {
            return;
        }
        //check the status message of the in process data request
        if (inProcessDataRequest.item2 !== 'Success' && inProcessDataRequest.item2 !== 'Success-External') {
            //error display
            modalService.error(inProcessDataRequest.item2);
            toastService.error(inProcessDataRequest.item2);
        } else {
            //success display and action
            const options: IModalConfirmOptions = {
                hideCancel: true,
                confirmText: 'OK',
                onConfirm: () => {
                    navigate(`/admindatarequests/active/${inProcessDataRequest.item1.id}/Studies`);
                }
            };
            modalService.confirm(`${successMessage}`, options);
            toastService.success(`${successMessage}`);
            //update the context
            updateDataRequest(inProcessDataRequest.item1);
        }
    }, [inProcessDataRequest]);

    if (!dataRequest.requestedStudies) {
        return <LoadIndicatorCenteredView />;
    }

    return (
        <div style={containerStyle}>
            <div style={sectionStyle}>
                <div style={headerContainerStyle}>
                    <FieldHeaderComponent title={`REQUESTED ${uppercaseStudiesString}`} />

                    <div style={buttonContainerStyle}>
                        {/* TODO this url will need to have both an amr and study version once it's built on the vivli site */}
                        {showQuestionMarkIcon()}

                        <ExportCsvButtonView
                            onClick={ipdDownloadAllowed ? downloadStudyFiles : downloadCsv}
                            title={
                                ipdDownloadAllowed
                                    ? `Download all ${t(TranslationKeyEnum.studies)} available for download as a single zip file`
                                    : `Download a list of the ${t(TranslationKeyEnum.studies)} in this request`
                            }
                            style={exportButtonStyle}
                        />
                    </div>
                    {showRequesterAddButton && (
                        <div style={buttonContainerStyle}>
                            <ButtonView
                                style={oneAcrossStyleSmallNoBox}
                                onClick={(e) => {
                                    addRequestedStudyDuringDraft();
                                }}
                                dataId={'add-study-during-draft-button'}
                            >
                                {addLabel}
                            </ButtonView>
                        </div>
                    )}
                </div>

                <div style={sectionStyle} />
                <div style={sectionStyle}>VIVLI-LISTED AND PROVISIONED {uppercaseStudiesString}</div>
                <div style={gridContainerStyle}>
                    {listedStudies.length > 0 ? (
                        <RequestedStudiesComponent
                            requestedStudies={listedStudies}
                            dataRequest={dataRequest}
                            onChange={handleDataRequestFormUpdate}
                        />
                    ) : (
                        noStudiesFound
                    )}
                </div>
            </div>
            <div style={sectionStyle}>
                <div style={sectionStyle}>VIVLI-LISTED {uppercaseStudiesString} PROVISIONED BY EXTERNAL PROVIDERS</div>
                <div style={gridContainerStyle}>
                    {externalStudies.length > 0 ? (
                        <RequestedStudiesComponent
                            requestedStudies={externalStudies}
                            dataRequest={dataRequest}
                            onChange={handleDataRequestFormUpdate}
                        />
                    ) : (
                        noStudiesFound
                    )}
                </div>
            </div>

            {!isAmr && (
                <>
                    <div style={sectionStyle}>
                        <div style={flexContainerStyle}>
                            <div>EXTERNAL DATA</div>
                            {editable && (isVivliAdmin || userIsOwner(dataRequest)) && (
                                <div data-test-id={DTIRequestedStudiesFeature.AddPlus} style={addStudyContainerStyle}>
                                    <ButtonView dataId={DTICommonConst.AddButton} onClick={addUnlistedStudy}>
                                        Add External Data
                                    </ButtonView>
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={gridContainerStyle}>
                        {unlistedStudies.length > 0 ? (
                            <RequestedStudiesComponent
                                requestedStudies={unlistedStudies}
                                dataRequest={dataRequest}
                                onChange={handleDataRequestFormUpdate}
                            />
                        ) : (
                            noStudiesFound
                        )}
                    </div>
                </>
            )}

            {canAddInProcess && (
                <div style={sectionStyle}>
                    <div style={sectionStyleWithTop}>{uppercaseStudiesString} TO ADD TO IN-PROCESS DATA REQUEST</div>
                    <AddStudyInProcessComponent
                        studySingularName={studyName}
                        studyPluralName={uppercaseStudiesString}
                        isAmr={isAmr}
                        addUnlistedStudy={addUnlistedStudy}
                        addInProcessStudy={addInProcessStudy}
                    />
                </div>
            )}
        </div>
    );
};
